import {
  FC,
  memo,
  SyntheticEvent,
  useState,
  useCallback,
  ComponentType,
  ImgHTMLAttributes,
} from 'react';
import styled from 'styled-components';

import ImagePlaceholder from '@components/loader/ImagePlaceholder';

const DefaultImage = styled.img<{ $isLoaded?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ $isLoaded }) => ($isLoaded ? 1 : 0.1)};
  transition: opacity 0.3s ease-in-out;
`;

const DefaultPlaceholder = styled(ImagePlaceholder)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

type ImageProps = ImgHTMLAttributes<HTMLImageElement> & { $isLoaded?: boolean };

type PlaceholderProps = Record<string, unknown>;

type Props = {
  image: string;
  background: string;
  onImageLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onImageError?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onBackgroundLoad?: (event: SyntheticEvent<HTMLImageElement>) => void;
  onBackgroundError?: (event: SyntheticEvent<HTMLImageElement>) => void;
  BackgroundComponent?: ComponentType<ImageProps>;
  ImageComponent?: ComponentType<ImageProps>;
  PlaceholderComponent?: ComponentType<PlaceholderProps>;
  backgroundProps?: Record<string, unknown>;
  imageProps?: Record<string, unknown>;
  placeholderProps?: Record<string, unknown>;
};

const ImageWithBackground: FC<Props> = ({
  image,
  background,
  onImageLoad,
  onImageError,
  onBackgroundLoad,
  onBackgroundError,
  BackgroundComponent = DefaultImage,
  ImageComponent = DefaultImage,
  PlaceholderComponent = DefaultPlaceholder,
  backgroundProps = {},
  imageProps = {},
  placeholderProps = {},
}) => {
  const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);
  const [isBackgroundLoaded, setIsBackgroundLoaded] = useState<boolean>(false);

  const handleImageLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      if (e.currentTarget.naturalWidth === 0) {
        onImageLoad?.(e);
      }
      setIsImageLoaded(true);
    },
    [onImageLoad],
  );

  const handleImageError = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      onImageError?.(e);
      setIsImageLoaded(true);
    },
    [onImageError],
  );

  const handleBackgroundLoad = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      if (e.currentTarget.naturalWidth === 0) {
        onBackgroundLoad?.(e);
      }
      setIsBackgroundLoaded(true);
    },
    [onBackgroundLoad],
  );

  const handleBackgroundError = useCallback(
    (e: SyntheticEvent<HTMLImageElement>) => {
      onBackgroundError?.(e);
      setIsBackgroundLoaded(true);
    },
    [onBackgroundError],
  );

  return (
    <>
      {(!isBackgroundLoaded || !isImageLoaded) && (
        <PlaceholderComponent {...placeholderProps} />
      )}

      <BackgroundComponent
        src={background}
        onLoad={handleBackgroundLoad}
        onError={handleBackgroundError}
        alt='background'
        loading='lazy'
        draggable='false'
        $isLoaded={isBackgroundLoaded}
        {...backgroundProps}
      />

      <ImageComponent
        src={image}
        onLoad={handleImageLoad}
        onError={handleImageError}
        alt='image'
        loading='lazy'
        draggable='false'
        $isLoaded={isImageLoaded}
        {...imageProps}
      />
    </>
  );
};

export default memo(ImageWithBackground);
